/**
 * S10 · Barcode scan.
 *
 * The screen has three states, and the whole point of the rewrite is that it
 * can tell them apart. It scans when the platform can decode and the camera
 * is live; it types when either of those is untrue; and it says why when it
 * has fallen back, rather than presenting a viewfinder that will never fire.
 *
 * Manual entry is not a fallback reachable only when `BarcodeDetector` is
 * absent. It is always one tap away, because the failures that matter in a
 * plant room — a denied permission, a damaged label, a detector that
 * advertises formats it cannot decode — all end with an inspector who can read
 * the code perfectly well and needs somewhere to put it.
 */
import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { Capture } from '@kapture/types';
import { Btn, Chip, GestureBar, I, Ico, StatusBar, T } from '@/components/primitives';
import { LayoutOrLeaf } from '@/components/LayoutOrLeaf';
import { useCamera } from '@/lib/device/useCamera';
import { useBarcodeScanner, type BarcodeHit } from '@/lib/device/useBarcodeScanner';
import { useGeolocation } from '@/lib/device/useGeolocation';
import {
  useCapturesBySession,
  useCaptureSessionStore,
  useCreateBarcodeMutation,
  useSyncStatus,
} from '@kapture/client-core';

export const Route = createFileRoute('/sessions/$sessionId/capture/barcode')({
  component: BarcodeCapturePage,
  staticData: { mobile: true, fullscreen: true },
});

function BarcodeCapturePage() {
  return (
    <LayoutOrLeaf routeId="/sessions/$sessionId/capture/barcode">
      <BarcodeCaptureContent />
    </LayoutOrLeaf>
  );
}

/** Has this walk already logged this code? */
function alreadyScanned(captures: Capture[] | undefined, value: string): boolean {
  return (captures ?? []).some((c) => c.kind === 'barcode' && c.value === value);
}

/** A short buzz on a hit — the inspector is looking at the label, not the screen. */
function buzz() {
  try {
    navigator.vibrate?.(40);
  } catch {
    // Not supported, or blocked by a permissions policy. Nothing to do.
  }
}

function BarcodeCaptureContent() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const camera = useCamera();
  const scanner = useBarcodeScanner();
  const geo = useGeolocation();
  const createBarcode = useCreateBarcodeMutation();
  const { online } = useSyncStatus();
  const { data: captures } = useCapturesBySession(sessionId);
  const setCurrentSubject = useCaptureSessionStore((st) => st.setCurrentSubject);
  const setActiveSession = useCaptureSessionStore((st) => st.setActiveSession);
  // Set by a scannable form field that sent us here; null when the dial did.
  const linkedFieldId = useCaptureSessionStore((st) => st.linkedFieldId);
  const setLinkedField = useCaptureSessionStore((st) => st.setLinkedField);

  const [hit, setHit] = useState<BarcodeHit | null>(null);
  const [manualValue, setManualValue] = useState('');
  const [typing, setTyping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [scanned, setScanned] = useState(0);

  const cameraFailed =
    camera.status === 'denied' || camera.status === 'unsupported' || camera.status === 'error';
  const scannerFailed = scanner.supported === false || scanner.status === 'error';
  const mustType = typing || cameraFailed || scannerFailed;

  // A GPS fix is worth having on every scan and costs nothing to ask for, so
  // it is requested regardless of which branch the screen ends up rendering.
  useEffect(() => {
    void geo.request({ timeout: 4000 }).catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The camera is started only once the probe says the platform can decode.
  // Opening a stream for a screen that is about to render a text input turns
  // on the OS camera indicator for nothing.
  useEffect(() => {
    if (scanner.supported !== true) return;
    void camera.start();
    return () => camera.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanner.supported]);

  // Arm the detector once there is actually a frame to read. `start` no-ops
  // when it is already polling, so this can safely re-run.
  useEffect(() => {
    if (scanner.supported !== true) return;
    if (camera.status !== 'ready' || !camera.videoRef.current) return;
    scanner.start(camera.videoRef.current, (next) => {
      buzz();
      setHit(next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.status, scanner.supported]);

  function close() {
    scanner.stop();
    camera.stop();
    if (linkedFieldId) {
      setLinkedField(null);
      navigate({
        to: '/sessions/$sessionId/form/$fieldId',
        params: { sessionId, fieldId: linkedFieldId },
      });
      return;
    }
    navigate({ to: '/sessions/$sessionId', params: { sessionId } });
  }

  /**
   * Write the scan, and make it the thing everything captured next is of.
   *
   * Setting the subject is not a second decision the inspector has to make.
   * A code scanned mid-walk is, by the act of scanning it, what the next
   * photographs are about — making that a separate tap would cost forty extra
   * taps on a school round and be forgotten on some of them, which is the
   * silent-misattribution failure the subject exists to prevent. A session
   * with only one thing in it is unharmed: the chip simply names it.
   *
   * `stay` is the difference between a rack of assets and a single one. On a
   * rack the inspector wants the viewfinder back immediately; on a single
   * asset they want to see what was written.
   */
  async function confirm(value: string, format: string | null, stay = false) {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const capture = await createBarcode.mutateAsync({
        sessionId,
        value,
        format,
        linkedFieldId,
        // Explicit, so the scan is filed under itself without the ambient
        // subject having to be moved first. An explicit value beats ambient
        // state in `withSubject`, which is what makes the ordering below safe.
        subjectRef: value,
        gps: geo.point ?? null,
      });
      // Only once the scan is actually written does it become the context for
      // what follows. Setting it first meant a failed write left every later
      // capture filed under a door whose scan does not exist — the screen said
      // it had not saved, and then quietly mis-filed the next ten photographs
      // anyway. That is the exact failure the subject exists to prevent.
      setActiveSession(sessionId);
      setCurrentSubject(value);
      setScanned((n) => n + 1);
      if (stay) {
        setHit(null);
        setManualValue('');
        scanner.rearm();
        return;
      }
      scanner.stop();
      camera.stop();
      if (linkedFieldId) {
        // A field sent us here, so the answer goes back to the field rather
        // than to a capture detail the inspector did not ask for. Cleared on
        // the way out: a stale one would attach the next dial-started scan to
        // a field nobody chose.
        setLinkedField(null);
        navigate({
          to: '/sessions/$sessionId/form/$fieldId',
          params: { sessionId, fieldId: linkedFieldId },
        });
        return;
      }
      navigate({
        to: '/sessions/$sessionId/captures/$captureId',
        params: { sessionId, captureId: capture.id },
      });
    } catch (err) {
      // The write is local-first, so a failure here is a broken device store
      // rather than a dropped connection — and silently flipping the button
      // back to "attach" would read as "nothing happened", not "it did not save".
      setSaveError(err instanceof Error ? err.message : 'Could not save the scan.');
    } finally {
      setSaving(false);
    }
  }

  if (scanner.supported === null && !typing) {
    return <Probing close={close} />;
  }

  if (mustType) {
    return (
      <ManualEntry
        close={close}
        saving={saving}
        saveError={saveError}
        reason={fallbackReason({ typing, cameraFailed, scannerFailed, camera, scanner })}
        canGoBackToScanning={typing && !cameraFailed && !scannerFailed}
        onBackToScanning={() => setTyping(false)}
        onSubmit={confirm}
        value={manualValue}
        setValue={setManualValue}
      />
    );
  }

  return (
    <div style={hudShell}>
      <video
        ref={camera.videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          background: '#000',
        }}
      />

      <div style={{ position: 'relative', zIndex: 10 }}>
        <StatusBar translucent />
      </div>
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <button type="button" onClick={close} aria-label="Close" style={hudIconBtn}>
          <Ico d={I.close} size={16} />
        </button>
        <Chip tone="hud" size="md">
          {scanned > 0
            ? `${scanned} scanned${online ? '' : ' · offline'}`
            : online
              ? 'Scan · QR / barcode'
              : 'Scan · offline'}
        </Chip>
        {camera.torchAvailable ? (
          <button
            type="button"
            onClick={() => void camera.setTorch(!camera.torchOn)}
            aria-label={camera.torchOn ? 'Turn torch off' : 'Turn torch on'}
            aria-pressed={camera.torchOn}
            style={{
              ...hudIconBtn,
              background: camera.torchOn ? T.brand : T.hudChip,
              color: camera.torchOn ? '#000' : '#fff',
            }}
          >
            <Ico d={I.flash} size={16} stroke={camera.torchOn ? '#000' : '#fff'} />
          </button>
        ) : (
          // No torch on this device — an inert glyph in a HUD reads as a broken
          // button, so the slot is held open rather than filled with one.
          <div style={{ width: 36 }} />
        )}
      </div>

      <div
        style={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 5,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at center, transparent 100px, rgba(0,0,0,0.7) 180px)',
          }}
        />
        <Reticle />
      </div>

      {hit ? (
        <div style={sheet}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 10,
              color: T.goodText,
              fontFamily: T.mono,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 6,
            }}
          >
            <Ico d={I.check2} size={12} stroke={T.goodText} sw={2.5} />
            Match found · {hit.format}
          </div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              fontFamily: T.mono,
              letterSpacing: '-0.01em',
              wordBreak: 'break-all',
            }}
          >
            {hit.rawValue}
          </div>
          {alreadyScanned(captures, hit.rawValue) && !saveError && (
            /*
             * Not a block, and not an error. Re-scanning a code is a normal
             * thing to do once a scan sets context — it means "I am back at
             * this door" — but on a forty-door round it is also how you lose
             * your place, so the screen says which of the two it might be
             * rather than silently writing a second identical capture.
             */
            <div
              style={{
                fontSize: 12,
                color: T.warnText,
                fontFamily: T.font,
                marginTop: 4,
                lineHeight: 1.4,
              }}
            >
              Already scanned on this session — attaching again returns you to it.
            </div>
          )}
          {saveError ? (
            <div style={{ fontSize: 12, color: T.dangerText, fontFamily: T.font, marginTop: 6 }}>
              {saveError}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: T.textDim, fontFamily: T.font, marginTop: 2 }}>
              Tap attach to link this code to the capture.
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <Btn
              tone="light"
              size="sm"
              onClick={() => {
                // Suppress this value rather than clearing outright: the code
                // is still in frame, and a plain reset re-offers it instantly.
                scanner.dismiss(hit.rawValue);
                setHit(null);
                setSaveError(null);
              }}
            >
              Not it
            </Btn>
            <Btn
              tone="primary"
              size="sm"
              full
              disabled={saving}
              onClick={() => void confirm(hit.rawValue, hit.format)}
              style={{ opacity: saving ? 0.5 : 1 }}
            >
              {saving ? 'Saving…' : 'Attach & review'}
            </Btn>
          </div>
          <Btn
            tone="light"
            size="sm"
            full
            disabled={saving}
            onClick={() => void confirm(hit.rawValue, hit.format, true)}
            style={{ marginTop: 6, opacity: saving ? 0.5 : 1 }}
          >
            Attach &amp; scan another
          </Btn>
        </div>
      ) : (
        <div style={{ position: 'relative', zIndex: 10, margin: '0 16px 14px' }}>
          <div style={hudHint}>
            {camera.status === 'requesting'
              ? 'Starting the camera…'
              : 'Point the reticle at a QR or barcode — detection is automatic.'}
          </div>
          <button type="button" onClick={() => setTyping(true)} style={typeInsteadBtn}>
            <Ico d={I.note} size={13} stroke="rgba(255,255,255,0.85)" />
            Type the code instead
          </button>
        </div>
      )}
      <GestureBar dark />
    </div>
  );
}

/**
 * Why the screen is asking someone to type.
 *
 * Named specifically rather than as one "can't scan" message: "you declined
 * the camera" and "this browser cannot decode" have different fixes, and an
 * inspector who does not know which one they hit cannot apply either.
 */
function fallbackReason(args: {
  typing: boolean;
  cameraFailed: boolean;
  scannerFailed: boolean;
  camera: ReturnType<typeof useCamera>;
  scanner: ReturnType<typeof useBarcodeScanner>;
}): { title: string; body: string } {
  const { typing, cameraFailed, scannerFailed, camera, scanner } = args;
  if (cameraFailed) {
    if (camera.status === 'denied') {
      return {
        title: 'Camera access is off',
        body: 'Grant camera access in your browser settings to scan, or type the code — either way the capture is written and queued for sync.',
      };
    }
    return {
      title: "The camera didn't start",
      body:
        camera.error ??
        'This device did not provide a camera stream. Type the code and the capture is written exactly as a scan would be.',
    };
  }
  if (scannerFailed) {
    return {
      title:
        scanner.status === 'error' ? "This browser can't decode" : "This browser can't scan codes",
      body:
        scanner.error ??
        "Safari and some older Android browsers don't expose the BarcodeDetector API. We'll still write the capture and queue it for sync.",
    };
  }
  if (typing) {
    return {
      title: 'Type the code',
      body: 'For a damaged or unreachable label. The capture is written exactly as a scan would be.',
    };
  }
  return { title: 'Type the code', body: '' };
}

function Probing({ close }: { close(): void }) {
  return (
    <div style={hudShell}>
      <StatusBar dark />
      <div style={hudHeader}>
        <button type="button" onClick={close} aria-label="Close" style={hudIconBtn}>
          <Ico d={I.close} size={16} />
        </button>
        <Chip tone="hud" size="md">
          Scan
        </Chip>
        <div style={{ width: 36 }} />
      </div>
      <div
        style={{
          flex: 1,
          display: 'grid',
          placeItems: 'center',
          color: 'rgba(255,255,255,0.6)',
          fontFamily: T.mono,
          fontSize: 12,
        }}
      >
        Checking what this device can decode…
      </div>
      <GestureBar dark />
    </div>
  );
}

function Reticle() {
  const corners = [
    { top: 0, left: 0, bt: true, bl: true, radius: '6px 0 0 0' },
    { top: 0, right: 0, bt: true, br: true, radius: '0 6px 0 0' },
    { bottom: 0, left: 0, bb: true, bl: true, radius: '0 0 0 6px' },
    { bottom: 0, right: 0, bb: true, br: true, radius: '0 0 6px 0' },
  ] as const;
  return (
    <div style={{ position: 'relative', width: 230, height: 230 }}>
      {corners.map((c, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: 28,
            height: 28,
            top: 'top' in c ? c.top : undefined,
            left: 'left' in c ? c.left : undefined,
            right: 'right' in c ? c.right : undefined,
            bottom: 'bottom' in c ? c.bottom : undefined,
            borderTop: 'bt' in c ? `3px solid ${T.brand}` : undefined,
            borderLeft: 'bl' in c ? `3px solid ${T.brand}` : undefined,
            borderRight: 'br' in c ? `3px solid ${T.brand}` : undefined,
            borderBottom: 'bb' in c ? `3px solid ${T.brand}` : undefined,
            borderRadius: c.radius,
          }}
        />
      ))}
      <div
        style={{
          position: 'absolute',
          left: 4,
          right: 4,
          top: '55%',
          height: 2,
          background: T.brand,
          boxShadow: `0 0 8px ${T.brand}`,
        }}
      />
    </div>
  );
}

const hudShell = {
  position: 'absolute',
  inset: 0,
  background: T.hudBg,
  color: '#fff',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: T.font,
  overflow: 'hidden',
} as const;

const hudHeader = {
  padding: '8px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
} as const;

const hudIconBtn = {
  width: 36,
  height: 36,
  borderRadius: 18,
  background: T.hudChip,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  border: 'none',
  backdropFilter: 'blur(8px)',
  cursor: 'pointer',
} as const;

const hudHint = {
  padding: '10px 12px',
  borderRadius: 12,
  background: 'rgba(0,0,0,0.55)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: 'rgba(255,255,255,0.85)',
  textAlign: 'center',
  fontSize: 12,
  fontFamily: T.font,
} as const;

const typeInsteadBtn = {
  marginTop: 8,
  width: '100%',
  padding: '10px 12px',
  borderRadius: 12,
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.22)',
  color: 'rgba(255,255,255,0.85)',
  fontSize: 12,
  fontFamily: T.font,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
} as const;

const sheet = {
  position: 'relative',
  zIndex: 10,
  margin: '0 16px 10px',
  padding: 14,
  background: 'rgba(255,255,255,0.98)',
  borderRadius: 14,
  boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
  color: T.text,
} as const;

function ManualEntry({
  close,
  saving,
  saveError,
  reason,
  canGoBackToScanning,
  onBackToScanning,
  onSubmit,
  value,
  setValue,
}: {
  close(): void;
  saving: boolean;
  saveError: string | null;
  reason: { title: string; body: string };
  canGoBackToScanning: boolean;
  onBackToScanning(): void;
  onSubmit(value: string, format: string | null): void;
  value: string;
  setValue(v: string): void;
}) {
  const canSubmit = value.trim().length > 0 && !saving;
  return (
    <div style={hudShell}>
      <StatusBar dark />
      <div style={hudHeader}>
        <button type="button" onClick={close} aria-label="Close" style={hudIconBtn}>
          <Ico d={I.close} size={16} />
        </button>
        <Chip tone="hud" size="md">
          Scan · manual
        </Chip>
        <div style={{ width: 36 }} />
      </div>
      <div
        style={{
          flex: 1,
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              opacity: 0.6,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 8,
            }}
          >
            {reason.title}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>
            Type or paste the code
          </div>
          {reason.body && (
            <div
              style={{
                fontSize: 13,
                marginTop: 6,
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.5,
              }}
            >
              {reason.body}
            </div>
          )}
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) onSubmit(value.trim(), null);
          }}
          placeholder="e.g. BR-WB-P3-0414"
          aria-label="Code"
          autoFocus
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          style={{
            padding: '14px 16px',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.08)',
            color: '#fff',
            fontFamily: T.mono,
            fontSize: 16,
            outline: 'none',
          }}
        />
        {saveError && (
          <div style={{ fontSize: 13, color: T.dangerText, fontFamily: T.font }}>{saveError}</div>
        )}
        <Btn
          tone="primary"
          size="lg"
          full
          disabled={!canSubmit}
          onClick={() => onSubmit(value.trim(), null)}
          style={{ opacity: canSubmit ? 1 : 0.5 }}
        >
          {saving ? 'Saving…' : 'Attach to capture'}
        </Btn>
        {canGoBackToScanning && (
          <button type="button" onClick={onBackToScanning} style={typeInsteadBtn}>
            <Ico d={I.camera} size={13} stroke="rgba(255,255,255,0.85)" />
            Back to scanning
          </button>
        )}
      </div>
      <GestureBar dark />
    </div>
  );
}
